import { useEffect, useState } from 'react'
import qs from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router'
import { Helmet } from 'react-helmet'
import NavBar from 'components/navbar/NavBar'
import ReportPanel from 'components/report/ReportPanel'
import ExportReportModal from 'components/modals/ExportReportModal'
import WhatIsThisReportModal from 'components/modals/WhatIsThisReportModal'
import * as reportActions from 'store/report/actions'
import { selectHasMath } from 'store/report/selectors'
import * as uiActions from 'store/ui/actions'
import styles from './styles/ReportApp.css'
import { groupSimilarNotices } from '../components/misc/EditorialNotices'
import * as analytics from 'lib/analytics'

import reportStore from 'store/report'
import loginModalStore from 'store/login-modal'
import injectAsyncStore from 'lib/inject-async-store'
import { startPosthogSessionRecording } from '../lib/posthog'

/**
 * Fetch data from API
 * */
export function fetchData (
  paperId,
  {
    dispatch,
    paper,
    citations,
    user,
    metadata
  }
) {
  const paperDoi = paper?.doi
  const paperSlug = paper?.slug || ''
  const urlDoi = decodeURIComponent(paperId).toLowerCase()

  // For slugs we want to disregard the first bit
  const paperSlugId = paperSlug.split('-').pop()
  const slugId = paperId.split('-').pop()

  const paperIdChanged = (paperId && (!paper || (paperDoi !== urlDoi && paperSlugId !== slugId)))
  const needCitations = (!citations || citations?.length === 0 || (user && metadata?.restrictedCitationCount))

  if (paperIdChanged) {
    dispatch(reportActions.resetFilters())
  }

  if (paperIdChanged || needCitations) {
    dispatch(reportActions.fetchReport(paperId))

    if (!user) {
      dispatch(reportActions.fetchCitingCounts(paperId))
    }
  }

  if (!user && ((citations?.length || 0) > 0) && metadata?.restrictedCitationCount) {
    dispatch(reportActions.fetchCitingCounts(paperId))
  }
}

const structuredDataFromPaper = (paper) => {
  const authorNames = paper.authors
    ? paper.authors.map(author => typeof author === 'string' ? author : `${author.given || ''} ${author.family || ''}`.trim())
    : []
  return JSON.stringify({
    '@context': 'http://schema.org',
    '@graph': [
      {
        '@id': '#issue',
        '@type': 'PublicationIssue',
        issueNumber: paper?.issue,
        datePublished: paper?.year,
        isPartOf: {
          '@id': '#periodical',
          '@type': [
            'PublicationVolume',
            'Periodical'
          ],
          name: paper?.journal || paper?.shortJournal,
          issn: paper?.issns,
          volumeNumber: paper?.volume,
          publisher: paper?.publisher
        }
      },
      {
        '@type': 'ScholarlyArticle',
        isPartOf: '#issue',
        description: paper?.abstract,
        abstract: paper?.abstract,
        sameAs: `https://doi.org/${paper?.doi}`,
        pagination: paper?.page,
        name: paper?.title,
        headline: paper?.title,
        author: authorNames,
        publisher: {
          '@type': 'Organization',
          name: paper?.publisher
        },
        datePublished: paper?.year
      }
    ]
  })
}

const ReportMetadata = ({ paper, tally, paperSlug }) => {
  if (!paper) {
    return null
  }
  const [loadMathJax, setLoadMathJax] = useState(false)
  const title = paper?.title
  const abstract = paper?.abstract
  const authorNames = paper?.authors
    ? paper?.authors.map(author => typeof author === 'string' ? author : `${author.family || ''}, ${author.given || ''}`.trim())
    : []
  const structuredData = structuredDataFromPaper(paper)
  const titleCopy = `[citation report] ${title || (abstract || '').substring(0, 150)}`
  const editorialNotices = groupSimilarNotices(paper.editorialNotices)?.map(({ status, date }) => ` ${status} (${date})`)?.join(' - ') || ''
  const contrasting = tally?.contradicting ? `Contrasting: ${tally?.contradicting}, ` : ''
  const supporting = tally?.supporting ? `Supporting: ${tally?.supporting}, ` : ''
  const mentioning = tally?.mentioning ? `Mentioning: ${tally?.mentioning} - ` : ''
  const descriptionBody = abstract || `${title} - ${authorNames?.join(', ')}`
  const descriptionCopy = `${editorialNotices} ${supporting}${contrasting}${mentioning}${descriptionBody}`.trim()
  const hasMath = useSelector(({ report }) => selectHasMath(report))

  useEffect(() => {
    analytics.event({
      category: 'Report',
      action: 'View Report',
      member_id: paper.memberId
    })
  }, [])

  useEffect(() => {
    if (hasMath) {
      setLoadMathJax(true)
    }
  }, [hasMath])

  return (
    <Helmet>
      {/* meta tags to avoid search engine cache so that tally information can update */}
      <title>{titleCopy}</title>
      <meta property='title' content={titleCopy} />
      <meta property='og:title' content={titleCopy} />
      <meta property='twitter:title' content={titleCopy} />

      <meta property='og:image' content={`http://image.thum.io/get/ogImage/https://scite.ai/reports/${paperSlug}?bannerClosed=true&onboardingOff=true&paywallOff=true&v=2`} />
      <meta property='twitter:image' content={`http://image.thum.io/get/ogImage/https://scite.ai/reports/${paperSlug}?bannerClosed=true&onboardingOff=true&paywallOff=true&v=2`} />

      <meta name='description' content={descriptionCopy} />
      <meta property='og:description' content={descriptionCopy} />
      <meta property='twitter:description' content={descriptionCopy} />
      <meta property='og:url' content={`https://scite.ai/reports/${paperSlug}`} />

      {/* Use the paper slug as the canonical source (since we have a duplicate with DOI) to avoid duplicate indexing */}
      <link rel='canonical' href={`https://scite.ai/reports/${paperSlug}`} />

      {/* A representation of the report as structured data for semantic applications such as google rich snippets. */}
      <script type='application/ld+json'>
        {structuredData}
      </script>
      {/* Meta tags for google scholar indexing and Zotero. See: https://scholar.google.ca/intl/en/scholar/inclusion.html#indexing */}
      <meta name='citation_title' content={title} />
      <meta name='citation_doi' content={paper?.doi} />
      {authorNames.map(name => (<meta key={name} name='citation_author' content={name} />))}
      <meta name='citation_year' content={paper?.year} />
      <meta name='citation_journal_title' content={paper?.journal} />
      <meta name='citation_volume' content={paper?.volume} />
      <meta name='citation_issue' content={paper?.issue} />
      <meta name='citation_abstract' content={paper?.abstract} />
      <meta name='citation_public_url' content={`https://doi.org/${paper?.doi}`} />

      {loadMathJax && (
        <script
          type='text/javascript'
          id='MathJax-script'
          defer
          async
          src='https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-svg.js'
        />
      )}
    </Helmet>
  )
}

export const ReportApp = ({
  match: { params: { doi } },
  location: { search, pathname }
}) => {
  const dispatch = useDispatch()
  const metadata = useSelector(({ report }) => report.metadata)
  const paper = useSelector(({ report }) => report.papers[report.paper])
  const paperDoi = useSelector(({ report }) => report.paper)
  const paperLoaded = useSelector(({ report }) => report.paperLoadTracker?.state === 'loaded')
  const paperLoadKey = useSelector(({ report }) => report.paperLoadTracker?.key)
  const tally = useSelector(({ report }) => report.tallies[report.tally])
  const citations = useSelector(({ report }) => report.citations)
  const showMobile = useSelector(({ report }) => report.filters.showMobile)
  const user = useSelector(({ ui }) => ui.user)
  const hasUser = useSelector(({ ui }) => ui.hasUser)
  const fetchingUser = useSelector(({ ui }) => ui.fetchingUser)
  const partner = useSelector(({ ui }) => ui.referralPartner)

  const paperSlug = paper?.slug || ''

  //
  // If we have loaded the target paper and the URL matches
  // the key it was loaded with, but not the paper's canonical slug
  // redirect to the slug.
  //
  const redirect = paperLoaded && paperLoadKey && paperSlug && paperLoadKey === decodeURIComponent(doi) && paperSlug !== doi

  useEffect(() => {
    if (user && user.onboarding && !user.onboarding.report?.initialOnboarding) {
      dispatch(uiActions.completeOnboardingStep({
        category: 'report',
        step: 'initial_onboarding',
        email: user.email
      }))

      startPosthogSessionRecording()
    }

    analytics.event({
      category: 'Report',
      action: 'View Report',
      label: `doi:${doi}`
    })
  }, [user])

  useEffect(() => {
    if (user) {
      //
      // This is to make sure we don't lose the filters
      // if there are some in the URL when the user logs in.
      // Since `report/RESET_FILTERS` does not actually
      // propagate to the URL.
      //
      const query = qs.parse(search, { arrayFormat: 'index' })
      dispatch(reportActions.handleQueryChange(query))
    }

    //
    // In this case there is an active user session
    // but we have not yet begun to fetch the user object
    //
    if (hasUser && !user && !fetchingUser) {
      return
    }

    if (!fetchingUser) {
      fetchData(doi, {
        dispatch,
        paper,
        citations,
        user,
        metadata
      })
    }
  }, [doi, user, fetchingUser, hasUser])

  useEffect(() => {
    if (partner) {
      dispatch(reportActions.fetchReport(doi))
    }
  }, [partner])

  useEffect(() => {
    const query = qs.parse(search, { arrayFormat: 'index' })
    dispatch(reportActions.handleQueryChange(query))
  }, [search])

  useEffect(() => {
    document.body.style.height = showMobile ? '100%' : 'auto'
    document.body.style.overflow = showMobile ? 'hidden' : 'auto'
  }, [showMobile])

  return (
    <div className={styles.layout}>
      <ReportMetadata paper={paper} paperSlug={paperSlug} tally={tally} />
      <NavBar />
      <div className={styles.app}>
        <ReportPanel />
      </div>
      {redirect &&
        <Redirect to={`/reports/${paperSlug}${search || ''}`} />}

      <ExportReportModal actionCreator={params => reportActions.exportReport(paperDoi, params)} />
      <WhatIsThisReportModal paper={paper} />
    </div>
  )
}

export const getInitialActions = ({ req: { cookies, query }, match: { params: { doi } } }) => {
  const decodedDoi = decodeURIComponent(doi)

  return [
    cookies.userSession ? reportActions.fetchPaper(decodedDoi) : reportActions.fetchReport(decodedDoi),
    reportActions.handleQueryChange(query)
  ]
}

export default injectAsyncStore({
  report: reportStore,
  loginModal: loginModalStore
})(ReportApp)
